// CSS
import './Search.css';
// React hooks
import { useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
// Skybase hooks
import { useAuth } from '../../context/AuthContext.js';
import { useData } from '../../context/DataContext.js';
import { useQuery } from '../../context/QueryContext.js';
// Skybase components
import Sidebar from '../../components/Sidebar/Sidebar.js';                                                          // sidebar
import SearchSuggestionInstance from './Elements/SearchSuggestionInstance/SearchSuggestionInstance';                // search suggestion instance
import AnimatedLogoOverlay from '../../components/AnimatedLogoOverlay/AnimatedLogoOverlay.js';                      // animated logo overlay
import LoadingScreenOverlay from '../../components/LoadingScreenOverlay/LoadingScreenOverlay.js';                   // loading screen overlay
import ExactKeywordMatchesToggle from '../../components/ExactKeywordMatchesToggle/ExactKeywordMatchesToggle.js';    // exact keyword matches toggle
import InformationalPopover from '../../components/Popovers/InformationalPopover/InformationalPopover.js';          // informational popover
// External hooks/components
import { useAuth as useClerkAuth } from '@clerk/react';
// Media assets
import SearchIconTransparent from '../../assets/StandardIcons/search-icon-transparent.png';                         // search icon
import StarsIconTransparent from '../../assets/StandardIcons/SearchModeIcons/ai-stars-icon.png'                     // ai stars icon
// target customer day



// ======
// Search – represents the '/search' Product page.
// ======
function Search(){
    
    // destructure global context
    const { isSubscribed, checkIsSubscribed, activeUserJson } = useAuth();
    const { activeEngineJson, isLoading, enableLoadingScreen, disableLoadingScreen } = useData();
    const { activeQuery, processSearchQuery, activeEngineSearchEvents, isKeywordIntent, setIsKeywordIntent } = useQuery();
    const { isLoading: isClerkLoading, getToken } = useClerkAuth();

    // init hooks
    const navigate = useNavigate();

    
    // ================
    // onMount Behavior
    // ================
    // onMount --> check for valid Skybase Stripe subscription
    const [isInformationalPopoverOpen, setIsInformationalPopoverOpen] = useState(false);
    useEffect(() => {
        
        // Async. fn that prevents CurrentUsers without an active subscription from interacting with this page.
        const checkSubscription = async () => {
            // Don't perform a subscription-check if the Clerk User hasn't loaded yet.
            if (isClerkLoading) return;

            // Does CurrentUser have an active subscription?
            const subscribed = await checkIsSubscribed();

            // If NO: Display InformationalPopover.
            if (!subscribed) { setIsInformationalPopoverOpen(true); }
        };

        // Run the async. subscription-check
        checkSubscription();

        // Disable any loading screens.
        disableLoadingScreen();

        // Edge case: "bfcache === true" --> Forcibly reload the page to re-run the async. subscription-check
        const handlePageShow = (event) => {
            if (event.persisted) { window.location.reload(); }
        };
        window.addEventListener("pageshow", handlePageShow); // "pageShow" event listener

        // Edge case: No <Clerk JWT> --> Immediately redirect user from this page.
        checkForValidClerkTokenOtherwiseRedirect();

        // Remove listeners onDismount (prevent memory overflow)
        return () => { window.removeEventListener("pageshow", handlePageShow); };
    }, [isClerkLoading])


    // ==========================================
    // checkForValidClerkTokenOtherwiseRedirect()
    // ==========================================
    // Checks for a valid clerk token. Forcibly re-directs the user to '/login' if not.
    async function checkForValidClerkTokenOtherwiseRedirect() {
        let token = await getToken();
        if (!token) { navigate('../login') };
    }
    // onChange ('activeUserJson') --> immediately check again for valid subscription
    useEffect(() => {
        checkForValidClerkTokenOtherwiseRedirect();
    }, [activeUserJson]);


    // ======================
    // engineQuerySuggestions – a String[] containing all of the Postgres search suggestion Strings for this particular Engine.
    // ======================
    const engineQuerySuggestions = activeEngineJson?.search_suggestions?.suggestions || [];
    

    // ======================
    // REANIMATION TRIGGERING
    // ======================
    useEffect(() => {
        // SEARCH PAGE FADE IN
        // Select all elements with this class
        const elements = document.querySelectorAll('.SearchPage-fade-in');

        elements.forEach(el => {
        // Remove animation class
        el.classList.remove('SearchPage-fade-in');

        // Force reflow
        void el.offsetWidth;

        // Re-add animation class
        el.classList.add('SearchPage-fade-in');
        });

        // Select all elements with this class
        const newE = document.querySelectorAll('.SearchPage-suggestions-repeater-instance');

        newE.forEach(el => {
        // Remove animation class
        el.classList.remove('SearchPage-suggestions-repeater-instance');

        // Force reflow
        void el.offsetWidth;

        // Re-add animation class
        el.classList.add('SearchPage-suggestions-repeater-instance');
        });
    }, [activeUserJson, activeEngineJson]); // Re-run whenever items change
    

    // ==========================
    // handleFormChange(e: Event) – update 'searchQuery' on every user form input change
    // ==========================
    const [searchQuery, setSearchQuery] = useState();
    const handleFormChange = (e) => {
        setSearchQuery(e.target.value);
    }

    
    // ====================================
    // Estimated Search Time (milliseconds) – adjust the est. search time (ms) based on 'isKeywordIntent'
    // ====================================
    const [estimatedSearchTimeMilliseconds, setEstimatedSearchTimeMilliseconds] = useState(1000); // estimated search time –> TARGET: 1s (1000ms)
    // onChange ('isKeywordIntent') -> adjust est. search time (ms)
    useEffect(() => {
        if (isKeywordIntent) {
            setEstimatedSearchTimeMilliseconds(600);    // Keyword: 600ms
        } else {
            setEstimatedSearchTimeMilliseconds(1000);   // Intelligent: 1s
        };
    }, [isKeywordIntent]);
    // sleep helper function
    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms)); // for sleeping 
    
    
    // ================
    // handleFormSubmit(e: Event) – async. orchestrator function triggered when CurrentUser submits a valid search query.
    // ================
    // track useRef to prevent /results jumpscare bug
    const isPageActiveRef = useRef(true);
    useEffect(() => {
        // The component/page isActive while mounted.
        isPageActiveRef.current = true;

        // The component/page !isActive when CurrentUser navigates away.
        return () => {
            // Component is unmounted
            isPageActiveRef.current = false;

            // Immediately disable the loading screen.
            disableLoadingScreen();
        };
    }, []);
    // handleFormSubmit(e: Event)
    const handleFormSubmit = async (e) => {
        e.preventDefault(); // prevent default HTML form behavior

        // extract the final search query
        const finalSearchQuery = queryTextRef.current || searchQuery

        // NEVER allow empty search queries
        if (!finalSearchQuery) { return };
        
        // process the search query
        processSearchQuery(finalSearchQuery);
        
        // enable loading screen
        enableLoadingScreen();

        // effort heuristic – hard-coded delay (processing time)
        await delay(estimatedSearchTimeMilliseconds);

        // Exit condition: CurrentUser left /search page while processing.
        if (!isPageActiveRef.current) { return }; // prevent re-direct

        // redirect user to /results page.
        navigate('/results', {
            state: {finalSearchQuery}
        });
    }

    // ===========================
    // handleSearchSuggestionClick(childText: String) – when a SearchSuggestionInstance isClicked, auto-trigger 'handleFormSubmit' with the SSI.text as the query.
    // ===========================
    // useRef vars
    const formRef = useRef(null);       // ref to the search form
    const queryTextRef = useRef("");    // used to IMMEDIATELY store the latest searchQuery 
    // handleSearchSuggestionClick(childText: String)
    const handleSearchSuggestionClick = (childText) => {
        
        // store the text in a ref so it doesn't wait for state updates
        queryTextRef.current = childText;

        // Trigger a REACT form submission using .requestSubmit();
        formRef.current.requestSubmit();
    };

    
    


    // DEMO-ONLY: Text stream used for recording product demos in Screen Studio.
    // const typeDemo = (text, speed = 30) => {
    //     let i = 0;

    //     const timer = setInterval(() => {
    //         i += 1;
    //         setSearchQuery(text.slice(0, i));

    //         if (i >= text.length) {
    //             clearInterval(timer);
    //         }
    //     }, speed);
    // };

    // const handleDemoFocus = () => {
    //     // Only run once
    //     if (searchQuery) return;

    //     typeDemo("Standard traffic pattern altitude", 30);
    // };
    

    // JSX Component
    return(
        <div className='Layout'>

            {/* Sidebar */}
            <Sidebar/>
        
            {/* Main content */}
            <div className='Search'>
                
                {/* STATE: !isLoading */}
                {!isLoading && 
                    <>

                    {/* Engine name */}
                    <div className='search-engine-name SearchPage-fade-in'>
                        <div className='search-engine-name__text' onClick={() => navigate('../search')}>

                            { activeEngineJson ? <>{activeEngineJson?.name}</> : <>Connecting to server..</>}
                            
                        </div>
                    </div>

                    {/* Advanced search bar */}
                    <div className={`search-advanced-search-bar ${!activeEngineJson ? 'disabled' : ''}`}>
                        <form 
                            id="search-form"
                            ref={formRef}
                            className='search-advanced-search-bar-form'
                            onSubmit={handleFormSubmit}
                        >
                            {/* search mode icon */}
                            {!isKeywordIntent && <img src={StarsIconTransparent} className='SearchPage-advanced-search-bar-ai-stars-icon' alt='Intelligent search mode is enabled.'/> }
                            {isKeywordIntent && <img src={ SearchIconTransparent } className={`search-advanced-search-bar-form__search-icon ${!activeEngineJson ? 'disabled' : ''}`} alt='Search Icon.'/> }
                            
                            {/* input */}
                            <input
                                name='searchQueryInput'
                                value={searchQuery}
                                onChange={handleFormChange}
                                // onFocus={handleDemoFocus} // DEMO-ONLY
                                className='search-advanced-search-bar-form__input'
                                placeholder={
                                    activeEngineJson
                                        ? (isKeywordIntent
                                            ? 'Enter the exact keyword(s) to find'
                                            : activeEngineJson.search_bar_placeholder_text || 'Connecting to the Skybase server..')
                                        : 'Connecting to the Skybase server..'
                                }
                            />
                        </form>
                    </div>

                    {/* Top 3 search suggestions */}
                    <>
                        {engineQuerySuggestions.map( (searchSuggestion, index) => (
                            <div
                                key={index}
                                className='SearchPage-suggestions-repeater-instance'
                                style={{ "--stagger-index": index }}>
                                <SearchSuggestionInstance
                                    suggestionQuery={searchSuggestion}
                                    suggestionType={ "trending" }
                                    onSearchSuggestionClick={handleSearchSuggestionClick}
                                    style={{ pointerEvents: 'auto' }} // child still receives events
                                />
                            </div>
                        ))}
                    </>
                </>}

                {/* STATE: isLoading */}
                {isLoading &&
                    <LoadingScreenOverlay
                        messagesToDisplay={[
                            "Analyzing search query",
                            "Extracting search vectors",
                            "Searching across database",
                            "Locating nearby vectors",
                            "Scanning indexed vectors",
                            "Evaluating distance metrics",
                            "Calculating similarity estimates",
                            "Accessing query relevance",
                            "Estimating match confidence",
                            "Processing search results",
                            "Filtering candidate results",
                            "Aggregating relevance scores",
                            "Ranking search results",
                            "Ranking top candidates",
                            ]}
                        estimatedTimeMilliseconds={estimatedSearchTimeMilliseconds}
                        currentActionMessage={`Now searching ${activeEngineJson?.name} for:`}
                        optionalActionMessage={`"${activeQuery}"`}
                    />
                }

                {/* Animated logo overlay */}
                <AnimatedLogoOverlay/>

                {/* Exact keyword matches toggle */}
                < ExactKeywordMatchesToggle />

            </div>

            {/* InformationalPopover */}
            { isInformationalPopoverOpen &&
                <InformationalPopover
                    titleText={"Subscription Required"}
                    bodyText={"Please purchase a Skybase subscription plan to continue."}
                    primaryButtonText={"View Subscription Plans"}
                    primaryButtonCallback={() => navigate('../pricing')}
                    secondaryButtonText={"Return Home"}
                    secondaryButtonCallback={() => navigate('../')}
                />
            }
        </div>
    );
}



// Export statement
export default Search;